// =============================================================================
// features/configuracoes/comercial/ImpactConfirmDialog.tsx — Confirmação
// obrigatória quando a mudança afeta campanha(s) ATIVA(s) (DoD: "Mudança que
// afeta campanha ativa deve mostrar o impacto antes de confirmar (não é
// fire-and-forget)"). DS §9.6 — modal elev-5, mesmo padrão de RuleModal
// (features/followup/FollowupRulesPage.tsx).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../components/ui/Button';

interface ImpactConfirmDialogProps {
  activeCampaignsCount: number;
  changedFields: string[];
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ImpactConfirmDialog({
  activeCampaignsCount,
  changedFields,
  isPending,
  onConfirm,
  onCancel,
}: ImpactConfirmDialogProps): React.JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[var(--text)]/60 backdrop-blur-[4px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="impact-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-md flex flex-col"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--warning)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
      >
        <div className="flex items-start gap-3 px-6 pt-5">
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-6 h-6 shrink-0"
            style={{ color: 'var(--warning)' }}
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h2
              id="impact-dialog-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-lg)', letterSpacing: '-0.03em' }}
            >
              Isso afeta {activeCampaignsCount} campanha{activeCampaignsCount !== 1 ? 's' : ''}{' '}
              ativa
              {activeCampaignsCount !== 1 ? 's' : ''}
            </h2>
            <p className="mt-1 font-sans text-ink-3" style={{ fontSize: 'var(--text-sm)' }}>
              A mudança vale para toda campanha em andamento imediatamente após salvar — não é
              possível aplicar só para campanhas futuras.
            </p>
          </div>
        </div>

        <div className="px-6 py-4">
          <span
            className="font-sans font-semibold uppercase tracking-wide text-ink-3"
            style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}
          >
            Campos alterados
          </span>
          <ul className="mt-2 flex flex-col gap-1">
            {changedFields.map((field) => (
              <li
                key={field}
                className="font-sans text-ink flex items-center gap-2"
                style={{ fontSize: 'var(--text-sm)' }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-pill shrink-0"
                  style={{ background: 'var(--warning)' }}
                  aria-hidden="true"
                />
                {field}
              </li>
            ))}
          </ul>
        </div>

        <div
          className="flex justify-end gap-3 px-6 py-4"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancelar
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Salvando…' : 'Confirmar e salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
}
